import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { addToCartTracking } from "@MasterBluePrint/tracking";

import CustomProductSelector, { ProductSelectorStyles } from "@MasterBluePrint/Components/CustomProductSelector";
import QuickMenu from "@MasterBluePrint/Components/QuickMenu";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: QuickBuyModalStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const settingsCollection = getSettingsCollection(state);
    return {
        canOrderUpload: settingsCollection.orderSettings.canOrderUpload,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        orderUploadPageNavLink: getPageLinkByPageType(state, "OrderUploadPage"),
        quickOrderPageNavLink: getPageLinkByPageType(state, "QuickOrderPage"),
        allowQuickOrder: settingsCollection.orderSettings.allowQuickOrder,
        state,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable(addToCart),
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface QuickBuyModalStyles {
    link?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    innerWrapper?: InjectableCss;
    PartNoGridItem?: GridItemProps;
    PartNoTextField?: TextFieldProps;
    orderUploadLink?: LinkPresentationProps;
    QtyGridItem?: GridItemProps;
    QtyTextField?: TextFieldProps;
    newListInput?: TextFieldProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addButton?: ButtonPresentationProps;
    productSelector?: ProductSelectorStyles;
    transition?: Transition;
}

export const quickBuyModalStyles: QuickBuyModalStyles = {
    modal: {
        size: 378,
        cssOverrides: {
            modalBody: css`
                overflow-y: visible;
            `,
            modalTitle: css`
                padding: .625rem 1.875rem;
            `,
            modalContent: css`
                padding: 1.25rem 1.875rem;
            `,
        },
    },

    container: { gap: 10 },
    PartNoGridItem: {
        width: 8,
        css: css`
            padding-right: .625rem;
        `,
    },
    QtyGridItem: {
        width: 2,
        css: css`
            padding-left: .625rem;
        `,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 1.875rem;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    addButton: {
        css: css`
            margin-left: .625rem;
        `,
    },
};

export const QuickBuyModalLink = ({
    showAddToCartConfirmationDialog,
    orderUploadPageNavLink,
    addToCart,
    allowQuickOrder,
    extendedStyles,
    state,
}: Props) => {
    const [styles] = useState(() => mergeToNew(quickBuyModalStyles, extendedStyles));
    const [errorMessage, setErrorMessage] = React.useState<React.ReactNode>("");

    return (
        <>
            <QuickMenu position="middle" isOpen={false}>
                <StyledWrapper>
                    <>
                        <CustomProductSelector
                            selectButtonTitle={translate("Add to Cart")}
                            productIsConfigurableMessage={siteMessage("QuickOrder_CannotOrderConfigurable")}
                            productIsUnavailableMessage={siteMessage("QuickOrder_ProductIsUnavailable")}
                            customErrorMessage={errorMessage}
                            extendedStyles={styles.productSelector}
                        />
                    </>
                </StyledWrapper>
            </QuickMenu>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickBuyModalLink);
